import { useState } from 'react';
import { getSupabaseClient } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { Footer } from '@ui/components/Footer';
import { SeekerHeader } from '@ui/components/SeekerHeader';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const supabase = getSupabaseClient();
      const redirectUrl = `${window.location.origin}/auth/reset`;

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        setError(error.message);
        setLoading(false);
      } else {
        setSuccess(true);
        setLoading(false);
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
      <div className="relative flex flex-1 w-full flex-col bg-[#FFFCEF]">
        <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
          <button
            type="button"
            onClick={handleBackToLogin}
            className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#FEF6D6]/30 bg-transparent text-xl text-[#FEF6D6] transition hover:bg-[#FEF6D6]/10"
            aria-label="Go back"
          >
            {'\u2190'}
          </button>
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
          </div>
          <div className="w-10" aria-hidden />
        </header>
        <div className="hidden lg:block">
          <SeekerHeader />
        </div>

        <div className="flex flex-1 flex-col items-center lg:px-[120px] lg:py-16">
          <main className="flex flex-1 lg:flex-initial min-h-0 flex-col items-center justify-center px-6 lg:px-0 w-full max-w-[480px]">
            <div className="w-full rounded-2xl border border-[#304031]/15 bg-[#FFFCEF] p-8 text-center">
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#304031]/10">
                <svg className="h-8 w-8 text-[#304031]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>

              <h1 className="mb-2 font-recoleta text-[28px] leading-[1.1] text-[#304031]">
                Reset Your Password
              </h1>
              <p className="mb-6 text-sm leading-relaxed text-[#304031]/70">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              {success ? (
                <div>
                  <div className="mb-6 rounded-2xl border border-[#4CAF50]/30 bg-[#E8F5E9] px-4 py-4 text-sm text-green-800">
                    <p className="font-medium">Check your email</p>
                    <p className="mt-1 text-green-700">
                      We've sent a password reset link to <strong>{email}</strong>.
                      Please check your inbox and click the link to reset your password.
                    </p>
                  </div>

                  <button
                    onClick={handleBackToLogin}
                    className="w-full rounded-full bg-[#304031] px-6 py-4 text-base font-semibold text-[#FEF6D6] transition hover:bg-[#304031]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF]"
                  >
                    Back to Login
                  </button>
                </div>
              ) : (
                <form className="space-y-4 text-left" onSubmit={handleSubmit}>
                  {error && (
                    <div className="rounded-2xl border border-[#B24E4E]/30 bg-[#FCEDEA] px-4 py-3 text-sm font-medium text-[#B24E4E]">
                      {error}
                    </div>
                  )}

                  <div>
                    <label className="mb-1 block text-sm font-medium text-[#304031]">
                      Email Address
                    </label>
                    <input
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full border border-[#304031]/30 rounded-lg px-3 py-2 text-sm bg-[#FFFCEF] text-[#304031] placeholder:text-[#304031]/40 focus:outline-none focus:ring-1 focus:ring-[#304031] focus:border-[#304031] transition-colors"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-base font-semibold transition ${
                      loading
                        ? 'cursor-not-allowed bg-[#304031]/60 text-[#FEF6D6]/80'
                        : 'bg-[#304031] text-[#FEF6D6] hover:bg-[#304031]/90'
                    } focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF]`}
                  >
                    {loading ? (
                      <>
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#FEF6D6] border-t-transparent"></div>
                        Sending...
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </button>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleBackToLogin}
                      className="text-sm text-[#304031]/70 transition-colors hover:text-[#304031]"
                    >
                      Back to Login
                    </button>
                  </div>
                </form>
              )}
            </div>
          </main>
        </div>
      </div>

      <div className="hidden lg:block">
        <Footer />
      </div>
    </div>
  );
}
